import { createContext, useContext, ReactNode, useState, useMemo, useCallback } from 'react';

export interface InfoDrawerContent {
  titleKey: string;
  subtitleKey?: string;
  content: ReactNode;
}

interface InfoDrawerContextType {
  isOpen: boolean;
  drawerContent: InfoDrawerContent | null;
  hasContent: boolean;
  openDrawer: (content: InfoDrawerContent) => void;
  closeDrawer: () => void;
  setContent: (content: InfoDrawerContent | null) => void;
  resetContent: () => void;
}

const InfoDrawerContext = createContext<InfoDrawerContextType | undefined>(undefined);

export const useInfoDrawer = () => {
  const context = useContext(InfoDrawerContext);
  if (context === undefined) {
    throw new Error('useInfoDrawer must be used within an InfoDrawerProvider');
  }
  return context;
};

interface InfoDrawerProviderProps {
  children: ReactNode;
}

export function InfoDrawerProvider({ children }: InfoDrawerProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [drawerContent, setDrawerContent] = useState<InfoDrawerContent | null>(null);

  const hasContent = Boolean(drawerContent);

  const openDrawer = useCallback((content: InfoDrawerContent) => {
    setDrawerContent(content);
    setIsOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setIsOpen(false);
  }, []);

  const setContent = useCallback((content: InfoDrawerContent | null) => {
    setDrawerContent(content);
  }, []);

  const resetContent = useCallback(() => {
    setDrawerContent(null);
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      drawerContent,
      hasContent,
      openDrawer,
      closeDrawer,
      setContent,
      resetContent,
    }),
    [isOpen, drawerContent, hasContent, openDrawer, closeDrawer, setContent, resetContent]
  );

  return <InfoDrawerContext.Provider value={value}>{children}</InfoDrawerContext.Provider>;
}
